import { all, call, delay, put, race, take, takeEvery } from 'redux-saga/effects';
import { deleteLock, editLock, getLockData, lockOffer } from '../../modules/lock-offer';
import { setLockedOffer } from '../rate-quote/slice';
import {
  closeLockModal,
  deleteLockFailure,
  deleteLockRequest,
  deleteLockSuccess,
  editLockRequest,
  getLockFailure,
  getLockRequest,
  getLockSuccess,
  lockOfferFailure,
  lockOfferRequest,
  lockOfferSuccess
} from './slice';

export function* handleLockOffer({ payload }: ReturnType<typeof lockOfferRequest>) {
  try {
    const { success, json, error } = yield call(lockOffer, payload);
    if (success) {
      yield put(lockOfferSuccess(json!));
      yield put(setLockedOffer({ offerId: payload.offerId, locked: true }));
      yield put(getLockRequest({ rateQuoteId: payload.rateQuoteId, offer: payload.offer }));
    } else {
      yield put(lockOfferFailure(error || 'error'));
    }
  } catch (err) {
    const error = err instanceof Error ? err.message : JSON.stringify(err);
    yield put(lockOfferFailure(error));
  }
}

export function* handleGetLock({
  payload: { rateQuoteId, offer }
}: ReturnType<typeof getLockRequest>) {
  yield race([
    // If the modal is closed shutdown this whole saga, we don't need the data.
    take(closeLockModal.type),
    // poll the getLockData endpoint till a non-pending
    // result is returned from the data.
    call(function* () {
      try {
        while (true) {
          const { success, json, error } = yield call(getLockData, {
            rateQuoteId,
            offerId: offer.offerId
          });
          if (success) {
            yield put(getLockSuccess(json));
            // poll
            if (!json.pending) {
              break;
            }
            // wait a second before re-polling;
            yield delay(1000);
          } else {
            yield put(getLockFailure(error || 'error'));
            break;
          }
        }
      } catch (err) {
        const error = err instanceof Error ? err.message : JSON.stringify(err);
        yield put(getLockFailure(error));
      }
    })
  ]);
}

export function* handleDeleteLock({ payload }: ReturnType<typeof deleteLockRequest>) {
  try {
    const { success, error } = yield call(deleteLock, payload);
    if (success) {
      yield put(deleteLockSuccess());
      yield put(setLockedOffer({ offerId: payload.offerId, locked: false }));
      yield put(closeLockModal());
    } else {
      yield put(deleteLockFailure(error!));
    }
  } catch (err) {
    const error = err instanceof Error ? err.message : JSON.stringify(err);
    yield put(deleteLockFailure(error));
  }
}

export function* handleEditLock({ payload }: ReturnType<typeof editLockRequest>) {
  try {
    const { success, json, error } = yield call(editLock, payload);
    if (success) {
      yield put(lockOfferSuccess(json!));
      yield put(getLockRequest({ rateQuoteId: payload.rateQuoteId, offer: payload.offer }));
    } else {
      yield put(lockOfferFailure(error || 'error'));
    }
  } catch (err) {
    const error = err instanceof Error ? err.message : JSON.stringify(err);
    yield put(lockOfferFailure(error));
  }
}

export const lockModalSaga = function* () {
  yield all([
    takeEvery(lockOfferRequest.type, handleLockOffer),
    takeEvery(getLockRequest.type, handleGetLock),
    takeEvery(deleteLockRequest.type, handleDeleteLock),
    takeEvery(editLockRequest.type, handleEditLock)
  ]);
};
